import CoverCardMVP from "../components/shared/CoverCardMVP";
import EmblaCarouselMVP from "../components/ui/Carousel/EmblaCarouselMVP";
import { Link } from "@tanstack/react-router";
import { Button } from "../components/ui/button";
import { getEventList } from "../api/api";
// eslint-disable-next-line no-duplicate-imports
import type { WorkshopListEndpoint } from "../api/api";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Skeleton } from "../components/ui/skeleton";
import claim_banner from "../assets/claim_banner.jpg";
import CoverCardNewVisual from "../components/shared/CoverCardNewVisual";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "./Home";
import { useAuth } from "@/context/AuthContext";
import { redirectionByAuthStatusByType } from "./Workshop/helpers";

const downloadPdf = (): void => {
	const fileUrl =
		"https://akademialtca.pl/materialy/Harmonogram_Akademia_LTCA.pdf";
	const link = document.createElement("a");
	link.href = fileUrl;
	link.download = "Harmonogram_Akademia_LTCA.pdf";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

const PER_PAGE = 5;

export const Upcoming = (): JSX.Element => {
	const { isAuthenticated } = useAuth();
	const { data: trainingData } = useInfiniteQuery<
		WorkshopListEndpoint,
		AxiosError
	>({
		queryKey: ["upcomingTrainingList", { per_page: PER_PAGE, page: 1 }],
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const result = await getEventList(
				{ search: "", category: "" },
				"training",
				"UPCOMING",
				Number(pageParam),
				PER_PAGE
			);
			if (!result) throw new Error("Failed to fetch events");
			return result;
		},
		getNextPageParam: (lastPage) => {
			const { page, total } = lastPage;
			const totalPages = Math.ceil(total / PER_PAGE); // Update based on `per_page`
			return page < totalPages ? page + 1 : undefined;
		},
	});

	const { data: webinarsData } = useInfiniteQuery<
		WorkshopListEndpoint,
		AxiosError
	>({
		queryKey: ["upcomingWebinarList", { per_page: PER_PAGE, page: 1 }],
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const result = await getEventList(
				{ search: "", category: "" },
				"webinar",
				"UPCOMING",
				Number(pageParam),
				PER_PAGE
			);
			if (!result) throw new Error("Failed to fetch events");
			return result;
		},
		getNextPageParam: (lastPage) => {
			const { page, total } = lastPage;
			const totalPages = Math.ceil(total / PER_PAGE); // Update based on `per_page`
			return page < totalPages ? page + 1 : undefined;
		},
	});

	const trainingItems = trainingData?.pages.flatMap((page) => page.items) ?? [];
	const webinarsItems = webinarsData?.pages.flatMap((page) => page.items) ?? [];
	const TRAINING_SLIDES = trainingItems.map((training) => (
		<Link
			{...redirectionByAuthStatusByType("training", training, isAuthenticated)}
		>
			<CoverCardNewVisual
				key={`CoverCardNewVisual-newarticles ${training.id}`}
				authors={training.authors_users_data}
				categories={training.kategoria}
				coverDate={Number(training.starts_at)}
				coverId={training.id}
				title={training.nazwa}
				slug={training.slug}
			/>
		</Link>
	));

	const WEBINARS_SLIDES = webinarsItems.map((webinar) => (
		<Link
			{...redirectionByAuthStatusByType("webinar", webinar, isAuthenticated)}
		>
			<CoverCardNewVisual
				key={`CoverCardNewVisual-newarticles ${webinar.id}`}
				authors={webinar.authors_users_data}
				categories={webinar.kategoria}
				coverDate={Number(webinar.starts_at)}
				coverId={webinar.id}
				title={webinar.nazwa}
				slug={webinar.slug}
			/>
		</Link>
	));

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto flex flex-col">
			{/* <div className="mb-5">
				<h2 className="text-base font-medium text-fontheader">
					Pobierz harmonogram szkoleń
				</h2>
				<div className="text-fontseconadry text-sm">
					<span>Data publikacji</span>
					<span className="px-3">&#8226;</span>
					<span>30.01.2024</span>
				</div>
			</div> */}
			<div className="flex justify-between items-center p-3 bg-seconadaryBackground rounded-xl mb-5 text-sm text-fontseconadry">
				<div>
					<h4 className="font-bold">Harmonogram najbliższych szkoleń</h4>
				</div>
				<Button
					className="border-[1px] border-slate-200 bg-gradient-to-r from-[#0059FF] to-[#20314F] text-white"
					variant="ghost"
					onClick={downloadPdf}
				>
					Pobierz
				</Button>
			</div>
			<div className="mb-5">
				<div className="flex justify-between items-center pb-3">
					<h2 className="text-base font-medium text-fontheader">Szkolenia</h2>
					<Link
						className="text-xs font-bold text-link"
						search={{ page: 1, per_page: 10, search: "" }}
						to="/workshops/training"
					>
						ZOBACZ WSZYSTKO
					</Link>
				</div>
				{TRAINING_SLIDES.length > 0 ? (
					<EmblaCarouselMVP
						options={{ align: "start", loop: true }}
						slides={TRAINING_SLIDES}
					/>
				) : (
					<Skeleton className="h-[215px] w-full pb-5 rounded-xl" />
				)}
			</div>
			<div className="flex justify-between items-center pb-3">
				<h2 className="text-base font-medium text-fontheader">Webinary</h2>
				<Link
					className="text-xs font-bold text-link"
					search={{ page: 1, per_page: 10, search: "" }}
					to="/workshops/training"
				>
					ZOBACZ WSZYSTKO
				</Link>
			</div>
			{WEBINARS_SLIDES.length > 0 ? (
				<EmblaCarouselMVP
					options={{ align: "start", loop: true }}
					slides={WEBINARS_SLIDES}
				/>
			) : (
				<Skeleton className="h-[215px] w-full pb-5 rounded-xl" />
			)}
			<div className="flex flex-1 justify-center items-center mt-5 mb-2">
				<img
					className="w-full rounded-xl"
					src={claim_banner}
					alt="claim banner"
				/>
			</div>
		</main>
	);
};
